import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Avatar,
  Button,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Import de jsPDF
import jsPDF from 'jspdf';

const OrderDetailsDialog = ({ open, onClose, selectedOrder, isIntervenant, apiUrl }) => {
  if (!selectedOrder) return null;

  // Récupération sécurisée de l'intervenant / praticien assigné
  const assignedUser = selectedOrder.intervenant || selectedOrder.user;
  const isPatient = Number(selectedOrder.client?.is_patient) === 1;

  // Fonction de génération du PDF 100% robuste
  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    // Couleurs principales
    const primaryColor = [67, 24, 255]; // #4318FF
    const darkColor = [43, 54, 116];   // #2B3674
    const grayColor = [112, 126, 174]; // #707EAE

    // En-tête de la facture
    doc.setFillColor(...darkColor);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURE', 14, 25);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Réf : ${selectedOrder.ref_cmd}`, 150, 20, { align: 'right' });
    doc.text(`Date : ${new Date(selectedOrder.created_at).toLocaleDateString('fr-FR')}`, 150, 28, { align: 'right' });

    // Informations Plateforme / Société
    doc.setTextColor(...darkColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Plateforme SOA', 14, 55);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayColor);
    doc.text('Coordination et Soins d\'Onco-Accompagnement', 14, 61);

    // Informations Client
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Facturé à :', 120, 55);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayColor);
    const clientName = `${selectedOrder.client?.nom_client || ''} ${selectedOrder.client?.prenom_client || ''}`;
    doc.text(clientName, 120, 61);
    doc.text(`Email : ${selectedOrder.client?.email_client || '—'}`, 120, 67);
    doc.text(`Tél : ${selectedOrder.client?.phone_client || '—'}`, 120, 73);
    doc.text(`Profil : ${isPatient ? 'Patient' : 'Client standard'}`, 120, 79);

    // Ligne de séparation
    doc.setDrawColor(220, 224, 242);
    doc.line(14, 88, 196, 88);

    // En-tête du tableau natif
    let currentY = 98;
    doc.setFillColor(...primaryColor);
    doc.rect(14, currentY, 182, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Description de la prestation', 18, currentY + 7);
    doc.text('Qté', 135, currentY + 7);
    doc.text('P.U.', 160, currentY + 7);
    doc.text('Total', 185, currentY + 7, { align: 'right' });

    currentY += 12;

    // Lignes des articles
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkColor);

    const items = selectedOrder.details_commandes?.length > 0
      ? selectedOrder.details_commandes
      : [{ label_offre_detcmd: 'Prestation / Offre', qnt_offre_detcmd: 1, pu_offre_detcmd: selectedOrder.total_ttc_cmd }];

    items.forEach((item, index) => {
      if (index % 2 === 1) {
        doc.setFillColor(244, 247, 254);
        doc.rect(14, currentY - 4, 182, 10, 'F');
      }
      doc.text(item.label_offre_detcmd || 'Prestation', 18, currentY + 2);
      doc.text(String(item.qnt_offre_detcmd || 1), 135, currentY + 2);
      doc.text(`${item.pu_offre_detcmd || 0} €`, 160, currentY + 2);
      const ligneTotal = (item.qnt_offre_detcmd || 1) * (item.pu_offre_detcmd || 0);
      doc.text(`${ligneTotal} €`, 190, currentY + 2, { align: 'right' });
      currentY += 10;
    });

    currentY += 5;
    doc.line(14, currentY, 196, currentY);
    currentY += 10;

    // Totaux financiers dans le PDF (HT -> TVA -> Total TTC -> Don -> Reste à payer)
    const rightMargin = 135;
    doc.setFontSize(10);
    doc.setTextColor(...grayColor);
    doc.text('Total Hors Taxe :', rightMargin, currentY);
    doc.text(`${Number(selectedOrder.total_htc_cmd || 0).toFixed(3)} €`, 190, currentY, { align: 'right' });

    currentY += 6;
    doc.text('TVA :', rightMargin, currentY);
    doc.text(`${Number(selectedOrder.total_tva_cmd || 0).toFixed(3)} €`, 190, currentY, { align: 'right' });

    currentY += 8;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkColor);
    doc.text('Total TTC :', rightMargin, currentY);
    doc.setTextColor(...primaryColor);
    doc.text(`${selectedOrder.total_ttc_cmd} €`, 190, currentY, { align: 'right' });

    if (isPatient) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);

      if (selectedOrder.prix_libre) {
        currentY += 6;
        doc.text('Prix libre / Don :', rightMargin, currentY);
        doc.text(`${selectedOrder.prix_libre} €`, 190, currentY, { align: 'right' });
      }
      const resteAPayer = selectedOrder.reste_a_payer_soa ?? selectedOrder.calcul_soa_praticien;
      if (resteAPayer !== undefined && resteAPayer !== null) {
        currentY += 6;
        doc.text('Reste à payer SOA :', rightMargin, currentY);
        doc.text(`${resteAPayer} €`, 190, currentY, { align: 'right' });
      }
    }

    // Informations Praticien / Intervenant si assigné
    if (assignedUser) {
      currentY += 15;
      doc.setDrawColor(220, 224, 242);
      doc.line(14, currentY, 196, currentY);
      currentY += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text('Intervenant / Praticien en charge :', 14, currentY);
      currentY += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);
      doc.text(`${assignedUser.nom_user} ${assignedUser.prenom_user} (${assignedUser.email_user || '—'})`, 14, currentY);
      currentY += 6;
      const mentionReversement = isPatient
        ? `Montant SOA calculé (sans commission) : ${selectedOrder.montant_soa_du_au_praticien || '0.00'} €`
        : `Montant net reversé (après commission de ${selectedOrder.taux_commission || '0'}%) : ${selectedOrder.montant_soa_du_au_praticien || '0.00'} €`;
      doc.text(mentionReversement, 14, currentY);
    }

    // Pied de page
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('Plateforme SOA - Document généré automatiquement', 105, 285, { align: 'center' });

    doc.save(`Facture_${selectedOrder.ref_cmd}.pdf`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: { xs: 2, md: 3 },
          boxShadow: '0px 20px 40px rgba(43, 54, 116, 0.1)',
        },
      }}
    >
      {/* --- HEADER --- */}
      <DialogTitle sx={{ p: 0, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              sx={{
                color: '#A3AED0',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              Détails complets de la commande
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#2B3674', fontSize: '26px' }}>
              {selectedOrder.ref_cmd}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ bgcolor: '#F4F7FE', '&:hover': { bgcolor: '#E0E7FF' } }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* --- CONTENU --- */}
      <DialogContent dividers sx={{ p: 0, border: 'none', my: 1 }}>
        
        {/* STATUT ET INFORMATIONS GENERALES */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: '16px',
            bgcolor: '#F4F7FE',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ color: '#A3AED0', fontSize: '12px', fontWeight: 600 }}>
              État de la transaction
            </Typography>
            <Typography sx={{ color: '#2B3674', fontWeight: 700, fontSize: '16px' }}>
              {selectedOrder.etat_cmd === 'SUCCESS' ? 'Transaction Réussie & Payée' : 'En attente de paiement'}
            </Typography>
          </Box>
          <Chip
            label={selectedOrder.etat_cmd}
            sx={{
              bgcolor: selectedOrder.etat_cmd === 'SUCCESS' ? '#05CD99' : '#FFB800',
              color: '#fff',
              fontWeight: 800,
              borderRadius: '8px',
              px: 1.5,
              py: 1,
            }}
          />
        </Paper>

        <Grid container spacing={3}>
          {/* COLONNE DE GAUCHE : CLIENT */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2, fontSize: '16px' }}>
              Informations Client
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', borderColor: '#E0E5F2', height: 'calc(100% - 36px)' }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: '#2B3674', fontWeight: 700, fontSize: '15px' }}>
                    {isIntervenant
                      ? `${selectedOrder.client?.prenom_client}`
                      : `${selectedOrder.client?.nom_client || ''} ${selectedOrder.client?.prenom_client || ''}`}
                  </Typography>
                  <Chip
                    icon={isPatient ? <LocalHospitalIcon sx={{ fontSize: '12px !important' }} /> : <PersonIcon sx={{ fontSize: '12px !important' }} />}
                    label={isPatient ? 'Patient' : 'Client'}
                    size="small"
                    sx={{
                      height: '22px',
                      fontSize: '11px',
                      fontWeight: 700,
                      bgcolor: isPatient ? '#EEF2FF' : '#F4F7FE',
                      color: isPatient ? '#4318FF' : '#A3AED0',
                    }}
                  />
                </Stack>
                {!isIntervenant && selectedOrder.client?.email_client && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon sx={{ fontSize: '16px', color: '#A3AED0' }} />
                    <Typography sx={{ color: '#707EAE', fontSize: '13px' }}>{selectedOrder.client.email_client}</Typography>
                  </Stack>
                )}
                {!isIntervenant && selectedOrder.client?.phone_client && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon sx={{ fontSize: '16px', color: '#A3AED0' }} />
                    <Typography sx={{ color: '#707EAE', fontSize: '13px' }}>{selectedOrder.client.phone_client}</Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarMonthIcon sx={{ fontSize: '16px', color: '#A3AED0' }} />
                  <Typography sx={{ color: '#707EAE', fontSize: '13px' }}>
                    Commandé le : {new Date(selectedOrder.created_at).toLocaleString('fr-FR')}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* COLONNE DE DROITE : INTERVENANT / PRATICIEN */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2, fontSize: '16px' }}>
              Intervenant / Praticien Assigné
            </Typography>
            {assignedUser ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', borderColor: '#E0E5F2', height: 'calc(100% - 36px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    src={assignedUser.photo_user ? `${apiUrl}/storage/workshop/${assignedUser.photo_user}` : undefined}
                    sx={{ width: 45, height: 45, bgcolor: '#4318FF', fontWeight: 700 }}
                  >
                    {assignedUser.prenom_user?.[0]}
                  </Avatar>
                  <Box>
                    <Typography sx={{ color: '#2B3674', fontWeight: 700, fontSize: '15px' }}>
                      {assignedUser.nom_user} {assignedUser.prenom_user}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#A3AED0', fontWeight: 600 }}>
                      {assignedUser.role_label || 'Praticien'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: '#A3AED0', fontSize: '13px' }}>Email pro :</Typography>
                    <Typography sx={{ color: '#2B3674', fontSize: '13px', fontWeight: 600 }}>{assignedUser.email_user || '—'}</Typography>
                  </Stack>
                  {!isPatient && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: '#A3AED0', fontSize: '13px' }}>Taux de commission :</Typography>
                      <Typography sx={{ color: '#4318FF', fontSize: '13px', fontWeight: 700 }}>
                        {selectedOrder.taux_commission !== undefined && selectedOrder.taux_commission !== null ? `${selectedOrder.taux_commission}%` : '—'}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', borderColor: '#E0E5F2', height: 'calc(100% - 36px)', display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: '#A3AED0', fontSize: '13px', fontStyle: 'italic' }}>
                  Aucun intervenant assigné.
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* DEUXIÈME LIGNE : ARTICLES & TARIFICATION / TOTAUX */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2, fontSize: '16px' }}>
              Articles & Tarification
            </Typography>
            
            {/* Liste des articles */}
            {selectedOrder.details_commandes?.map((item, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '14px', borderColor: '#E0E5F2' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography sx={{ color: '#2B3674', fontWeight: 700, fontSize: '14px' }}>
                      {item.label_offre_detcmd}
                    </Typography>
                    <Typography sx={{ color: '#A3AED0', fontSize: '12px', mt: 0.5 }}>
                      Type : {item.type_detail} | Qté : {item.qnt_offre_detcmd}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: '#4318FF', fontWeight: 800, fontSize: '15px' }}>
                    {item.pu_offre_detcmd}€
                  </Typography>
                </Stack>
              </Paper>
            ))}

            {/* Détails financiers additionnels */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px', bgcolor: '#FAFCFE', borderColor: '#E0E5F2' }}>
              <Stack spacing={1}>
                {selectedOrder.prix_libre && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: '#707EAE', fontSize: '13px' }}>Prix libre / Don client :</Typography>
                    <Typography sx={{ color: '#2B3674', fontSize: '13px', fontWeight: 700 }}>{selectedOrder.prix_libre}€</Typography>
                  </Stack>
                )}
                {isPatient && selectedOrder.prix_special_soa && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: '#707EAE', fontSize: '13px' }}>Prix spécial SOA :</Typography>
                    <Typography sx={{ color: '#2B3674', fontSize: '13px', fontWeight: 700 }}>{selectedOrder.prix_special_soa}€</Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2, fontSize: '16px' }}>
              Totaux & Reversement
            </Typography>

            {/* ENCADREMENT TOTAUX : HT -> TVA -> TTC -> (Prix Libre) -> (Reste à Payer SOA) */}
            <Box sx={{ p: 2.5, mb: 2, bgcolor: '#2B3674', borderRadius: '16px', color: '#fff' }}>
              <Stack spacing={1}>
                {/* 1. Total HT */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ opacity: 0.8, fontSize: '13px' }}>Total Hors Taxe</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{Number(selectedOrder.total_htc_cmd || 0).toFixed(3)}€</Typography>
                </Box>

                {/* 2. TVA */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ opacity: 0.8, fontSize: '13px' }}>TVA</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{Number(selectedOrder.total_tva_cmd || 0).toFixed(3)}€</Typography>
                </Box>

                <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.1)' }} />

                {/* 3. Total TTC (affiché tout de suite après la TVA) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>Total TTC</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#05CD99' }}>
                    {selectedOrder.total_ttc_cmd}€
                  </Typography>
                </Box>

                {/* 4. Si patient : Prix libre / Don et Reste à payer SOA affichés sous le Total TTC */}
                {isPatient && (
                  <>
                    <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                    {selectedOrder.prix_libre && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ opacity: 0.8, fontSize: '13px' }}>Prix libre / Don patient</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#FFB800' }}>{selectedOrder.prix_libre}€</Typography>
                      </Box>
                    )}
                    {(selectedOrder.reste_a_payer_soa !== undefined || selectedOrder.calcul_soa_praticien !== undefined) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ opacity: 0.8, fontSize: '13px' }}>Reste à payer SOA</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#05CD99' }}>
                          {selectedOrder.reste_a_payer_soa ?? selectedOrder.calcul_soa_praticien}€
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </Box>

            {/* CASE APARTE : MONTANT REVERSÉ AU PRATICIEN */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '16px',
                bgcolor: '#EEF2FF',
                border: '1px solid #C7D2FE',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ p: 1, bgcolor: '#4318FF', borderRadius: '10px', color: '#fff', display: 'flex' }}>
                      <AccountBalanceWalletIcon sx={{ fontSize: '20px' }} />
                    </Box>
                    <Typography sx={{ color: '#2B3674', fontWeight: 800, fontSize: '14px' }}>
                      Reversé au Praticien
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: '#4318FF', fontWeight: 900, fontSize: '20px' }}>
                    {selectedOrder.montant_soa_du_au_praticien ? `${selectedOrder.montant_soa_du_au_praticien}€` : '0.00€'}
                  </Typography>
                </Stack>
                <Typography sx={{ color: '#707EAE', fontSize: '12px', lineHeight: 1.5 }}>
                  {isPatient ? (
                    <>
                      Pour ce <strong>patient</strong>, il n'y a pas de commission : ce montant correspond au solde SOA calculé (après le don/prix libre du client).
                    </>
                  ) : (
                    <>
                      Ce montant représente la somme nette rendue au praticien <strong>après déduction de la commission</strong> fixée par la plateforme SOA ({selectedOrder.taux_commission || '0'}%).
                    </>
                  )}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* --- ACTIONS --- */}
      <DialogActions sx={{ p: 0, mt: 3, pt: 2, borderTop: '1px solid #F4F7FE' }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleDownloadPDF}
          sx={{
            borderRadius: '12px',
            px: 3,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 700,
            color: '#4318FF',
            borderColor: '#4318FF',
            '&:hover': { bgcolor: '#EEF2FF', borderColor: '#4318FF' }
          }}
        >
          Télécharger la facture (PDF)
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            borderRadius: '12px',
            px: 4,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#4318FF',
            '&:hover': { bgcolor: '#3311CC' }
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsDialog;